"use client"

import { FC, useEffect, useRef, useState } from "react"
import { Phone, X, MessageCircle } from "lucide-react"
import { FaTelegram, FaViber, FaWhatsapp } from "react-icons/fa"
import { useLanguage } from "@/src/context/LanguageContext"
import { useUI } from "@/src/lib/i18n/ui"

const FloatingMessenger: FC = () => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { lang } = useLanguage()
  const t = useUI(lang)

  const messengers = [
    {
      label: t("messengerCall"),
      href: "tel:+380636644132",
      icon: Phone,
      bg: "bg-white",
      text: "text-black",
      iconSize: "w-5 h-5",
    },
    {
      label: "WhatsApp",
      href: "https://wa.me/380636644132",
      icon: FaWhatsapp,
      bg: "bg-[#25D366]",
      text: "text-white",
      iconSize: "w-5 h-5",
    },
    {
      label: "Viber",
      href: "viber://chat?number=+380636644132",
      icon: FaViber,
      bg: "bg-[#7360F2]",
      text: "text-white",
      iconSize: "w-5 h-5",
    },
    {
      label: "Telegram",
      href: "https://t.me/+380636644132",
      icon: FaTelegram,
      bg: "bg-[#229ED9]",
      text: "text-white",
      iconSize: "w-5 h-5",
    },
  ]

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div
      ref={ref}
      className="fixed bottom-6 right-4 md:right-6 z-40 flex flex-col items-end gap-3"
    >
      {messengers.map((m, i) => {
        const Icon = m.icon
        const delay = (messengers.length - 1 - i) * 60
        return (
          <div
            key={m.label}
            className="flex items-center gap-2"
            style={{
              transform: open ? "translateY(0) scale(1)" : "translateY(16px) scale(0.85)",
              opacity: open ? 1 : 0,
              transition: `transform 220ms cubic-bezier(0.34,1.56,0.64,1) ${open ? delay : 0}ms, opacity 180ms ease ${open ? delay : 0}ms`,
              pointerEvents: open ? "auto" : "none",
            }}
          >
            <span className="bg-black/80 text-white text-xs font-light px-2.5 py-1 rounded-full border border-gray-800 whitespace-nowrap">
              {m.label}
            </span>
            <a
              href={m.href}
              target={m.href.startsWith("tel:") || m.href.startsWith("viber:") ? undefined : "_blank"}
              rel="noopener noreferrer"
              aria-label={m.label}
              className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg ${m.bg} ${m.text} hover:opacity-90 transition-opacity`}
            >
              <Icon className={m.iconSize} />
            </a>
          </div>
        )
      })}

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t("messengerClose") : t("messengerOpen")}
        aria-expanded={open}
        className="flex items-center justify-center w-14 h-14 rounded-full bg-white text-black shadow-xl hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
      >
        <span
          className="transition-transform duration-200"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
        >
          {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        </span>
      </button>
    </div>
  )
}

export default FloatingMessenger
